import { Box, Button, Container, Typography } from "@mui/material";
import { AlertTriangle } from "lucide-react";
import React from "react";
import { useNavigate, useRouteError } from "react-router-dom";

interface RouteError {
  message?: string;
  statusText?: string;
  status?: number;
}

/**
 * Error boundary component for route-level errors.
 * Displays a user-friendly error message and allows navigation back.
 * Logs errors to console for debugging.
 */
export const EventEditErrorBoundary: React.FC = () => {
  const error = useRouteError() as RouteError;
  const navigate = useNavigate();

  // Log error for debugging
  React.useEffect(() => {
    console.error("Event Edit Error:", error);
    
    // Send to error monitoring service (e.g., Sentry) if configured
    // if (window.Sentry) {
    //   window.Sentry.captureException(error);
    // }
  }, [error]);

  const handleGoBack = () => {
    navigate("/events", { replace: true });
  };

  const handleTryAgain = () => {
    navigate(0); // Reload the page
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          textAlign: "center",
          gap: 3,
        }}
      >
        <AlertTriangle size={64} color="#f44336" />
        
        <Typography variant="h4" gutterBottom>
          Oops! Algo deu errado
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Não foi possível carregar a página de edição do evento.
          {error?.message && (
            <>
              <br />
              <Typography
                variant="caption"
                component="span"
                sx={{ fontFamily: "monospace", mt: 1, display: "block" }}
              >
                Erro: {error.message}
              </Typography>
            </>
          )}
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGoBack}
          >
            Voltar para Eventos
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            onClick={handleTryAgain}
          >
            Tentar Novamente
          </Button>
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          Se o problema persistir, entre em contato com o suporte.
        </Typography>
      </Box>
    </Container>
  );
};

export default EventEditErrorBoundary;
